import moment from 'moment'

/* ── Publications ───────────────────────────────────────────────────────── */

const num2 = v => (v != null && v !== '' && Number.isFinite(parseFloat(v)) ? parseFloat(parseFloat(v).toFixed(2)) : '')

const POST_COLUMNS = {
  impressions:     { label: 'Impressions',          value: p => p.post_impressions || 0 },
  views:           { label: 'Vues',                 value: p => p.post_impressions || p.view_count || 0 },
  likes:           { label: 'Likes',                value: (p, network) => (network === 'tiktok' || network === 'linkedin' ? p.like_count || 0 : p.post_reactions_sum || p.like_count || 0) },
  comments:        { label: 'Commentaires',         value: p => p.comments_count || 0 },
  shares:          { label: 'Partages',             value: p => p.shares_count || 0 },
  clicks:          { label: 'Clics',                value: p => p.click_count || 0 },
  interactions:    { label: 'Interactions',         value: p => p.interactions || 0 },
  engagement_rate: { label: 'Taux engagement (%)',  value: p => num2(p.reach_engagement) },
  duration:        { label: 'Durée (s)',            value: p => p.duration || '' },
  time_watched:    { label: 'Temps regardé (s)',    value: p => p.time_watched || '' },
  completion:      { label: 'Taux complétion (%)',  value: p => (p.video_finish_rate != null ? parseFloat((p.video_finish_rate * 100).toFixed(1)) : '') },
}
const COLUMN_ORDER = Object.keys(POST_COLUMNS)

function postColumnKeys(network) {
  if (network === 'tiktok') return ['views', 'likes', 'comments', 'shares', 'engagement_rate', 'duration', 'time_watched', 'completion']
  if (network === 'linkedin') return ['impressions', 'likes', 'comments', 'shares', 'clicks', 'interactions']
  return ['impressions', 'likes', 'comments', 'shares', 'engagement_rate']
}

function sortedPosts(stats) {
  return ((stats && stats.posts) || []).slice().sort((a, b) => {
    const ta = a.created_time || a.date || 0
    const tb = b.created_time || b.date || 0
    return ta < tb ? 1 : ta > tb ? -1 : 0
  })
}

function postBaseCells(p) {
  return [
    p.created_time ? moment(p.created_time).format('YYYY-MM-DD HH:mm') : '',
    p.type || '',
    p.message || p.video_description || '',
    p.permalink_url || '',
  ]
}

// Publications d'une page (colonnes propres à son réseau)
export function buildPostsTable(stats, page) {
  const network = page ? page.network : ''
  const keys = postColumnKeys(network)
  return {
    headers: ['Date', 'Type', 'Message', 'URL', ...keys.map(k => POST_COLUMNS[k].label)],
    rows: sortedPosts(stats).map(p => [...postBaseCells(p), ...keys.map(k => POST_COLUMNS[k].value(p, network))]),
  }
}

// Publications de plusieurs pages : union des colonnes des réseaux présents
export function buildMultiPagePostsTable(items) {
  const used = new Set()
  items.forEach(({ page }) => postColumnKeys(page && page.network).forEach(k => used.add(k)))
  const keys = COLUMN_ORDER.filter(k => used.has(k))
  const rows = []
  items.forEach(({ page, stats }) => {
    const network = page ? page.network : ''
    const own = postColumnKeys(network)
    sortedPosts(stats).forEach(p => {
      rows.push([
        page.name || '', network,
        ...postBaseCells(p),
        ...keys.map(k => (own.includes(k) ? POST_COLUMNS[k].value(p, network) : '')),
      ])
    })
  })
  return {
    headers: ['Page', 'Réseau', 'Date', 'Type', 'Message', 'URL', ...keys.map(k => POST_COLUMNS[k].label)],
    rows,
  }
}

/* ── Concurrence (mêmes règles de calcul que ReportingConcurrent) ───────── */

export function concurrentPosts(row) {
  if (row.isOwnPage || !row.posts) return []
  if (Array.isArray(row.posts)) return row.posts
  try { return JSON.parse(row.posts) } catch (_) { return [] }
}
export function concurrentFollowers(row) { return row.pageFollowers || row.pageFans || 0 }
export function concurrentPostsCount(row) {
  return row.isOwnPage ? row.postsCount || 0 : concurrentPosts(row).length
}
export function concurrentReactions(row) {
  if (row.total_reactions != null) return row.total_reactions
  return concurrentPosts(row).reduce((s, p) => s + (p.reactions && p.reactions.summary ? p.reactions.summary.total_count : (p.like_count || p.reactions_count || 0)), 0)
}
export function concurrentComments(row) {
  if (row.total_comments != null) return row.total_comments
  return concurrentPosts(row).reduce((s, p) => s + (p.comments && p.comments.summary ? p.comments.summary.total_count : (p.comments_count || 0)), 0)
}
export function concurrentShares(row) {
  if (row.total_shares != null) return row.total_shares
  return concurrentPosts(row).reduce((s, p) => s + (p.shares && p.shares.count ? p.shares.count : 0), 0)
}
// Indice d'engagement : (réactions + commentaires + partages) / posts / abonnés × 1000
export function concurrentEngagementScore(row) {
  const f = concurrentFollowers(row)
  const n = concurrentPostsCount(row)
  if (!f || !n) return null
  return (concurrentReactions(row) + concurrentComments(row) + concurrentShares(row)) / n / f * 1000
}

function concurrentCells(row, network) {
  const score = concurrentEngagementScore(row)
  return [
    row.concurrent_name || row.network_id || '',
    row.isOwnPage ? 'Oui' : '',
    concurrentFollowers(row),
    concurrentPostsCount(row),
    concurrentReactions(row),
    concurrentComments(row),
    network === 'facebook' ? concurrentShares(row) : '',
    score == null ? '' : parseFloat(score.toFixed(2)),
  ]
}
const CONCURRENT_HEADERS = ['Page', 'Votre page', 'Abonnés', 'Publications', 'Réactions', 'Commentaires', 'Partages', 'Indice d\'engagement']

export function buildConcurrentTable(rows, network) {
  return {
    headers: CONCURRENT_HEADERS,
    rows: (rows || []).filter(r => !r.pending && !r.error).map(r => concurrentCells(r, network)),
  }
}

// concurrentMap : { [pageId]: rows } (cache de ReportingMain)
export function buildMultiPageConcurrentTable(items, concurrentMap) {
  const rows = []
  items.forEach(({ page }) => {
    const conc = (concurrentMap && concurrentMap[page.id]) || []
    conc.filter(r => !r.pending && !r.error).forEach(r => {
      rows.push([page.name || '', page.network || '', ...concurrentCells(r, page.network)])
    })
  })
  return { headers: ['Page de référence', 'Réseau', ...CONCURRENT_HEADERS], rows }
}

/* ── Sérialisation ──────────────────────────────────────────────────────── */

// sections : [{ title, headers, rows }] — séparées par une ligne vide
export function toCsv(sections) {
  const escape = v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`
  const line = cells => cells.map(escape).join(',')
  const blocks = sections.map(s => [line([s.title]), line(s.headers), ...s.rows.map(line)].join('\n'))
  return '﻿' + blocks.join('\n\n')
}

// Classeur SpreadsheetML (XML Excel 2003) : vrais onglets et cellules numériques, sans dépendance
export function toExcelXml(sheets) {
  const esc = v => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  const cell = (v, style) => {
    const st = style ? ` ss:StyleID="${style}"` : ''
    if (typeof v === 'number' && Number.isFinite(v)) return `<Cell${st}><Data ss:Type="Number">${v}</Data></Cell>`
    return `<Cell${st}><Data ss:Type="String">${esc(v == null ? '' : v)}</Data></Cell>`
  }
  const usedNames = {}
  // Nom d'onglet Excel : 31 caractères max, sans []:*?/\ et unique
  const sheetName = name => {
    const base = String(name || 'Feuille').replace(/[[\]:*?/\\]/g, ' ').slice(0, 28) || 'Feuille'
    let n = base
    let i = 2
    while (usedNames[n]) n = `${base} ${i++}`
    usedNames[n] = true
    return n
  }
  const worksheets = sheets.map(s => {
    const head = s.headers ? `<Row>${s.headers.map(h => cell(h, 'head')).join('')}</Row>` : ''
    const body = s.rows.map(r => `<Row${r.style ? ` ss:StyleID="${r.style}"` : ''}>${(r.cells || r).map(c => cell(c, r.style)).join('')}</Row>`).join('')
    return `<Worksheet ss:Name="${esc(sheetName(s.name))}"><Table>${head}${body}</Table></Worksheet>`
  }).join('')
  return `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Styles>
<Style ss:ID="head"><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#6B1F4D" ss:Pattern="Solid"/></Style>
<Style ss:ID="bold"><Font ss:Bold="1"/></Style>
</Styles>
${worksheets}
</Workbook>`
}

export function downloadBlob(content, type, filename) {
  const url = URL.createObjectURL(new Blob([content], { type }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export const CSV_MIME = 'text/csv;charset=utf-8'
export const EXCEL_MIME = 'application/vnd.ms-excel;charset=utf-8'

export function slug(name) {
  return String(name || 'page').replace(/[^a-z0-9]/gi, '-').toLowerCase()
}
